// components/RetailerProfileModal.jsx
import React, { useState, useEffect } from 'react';
import { apiCall } from '../utils/api';
import toast from 'react-hot-toast';

const RetailerProfileModal = ({ isOpen, onClose, userId }) => {
    const [loading, setLoading] = useState(false);
    const [profileData, setProfileData] = useState(null);
    const [error, setError] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [updating, setUpdating] = useState(false);
    const [activeTab, setActiveTab] = useState('basic');

    useEffect(() => {
        if (isOpen && userId) {
            fetchRetailerProfile();
        }
    }, [isOpen, userId]);

    const fetchRetailerProfile = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiCall(
                "retailer-profile",
                {
                    user_id: userId
                },
                true
            );

            if (response.status === true) {
                setProfileData(response.data);
                setSelectedStatus(response.data.status || '');
            } else {
                setError(response.message || "Failed to fetch retailer profile");
                toast.error(response.message || "Failed to fetch retailer profile");
            }
        } catch (error) {
            setError("Network error. Please try again.");
            toast.error("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async () => {
        if (!selectedStatus) {
            toast.error("Please select a status");
            return;
        }

        setUpdating(true);
        try {
            const response = await apiCall(
                "update-retailer-status",
                {
                    user_id: profileData.user_id,
                    status: selectedStatus
                },
                true
            );

            if (response.status === true) {
                toast.success("Status updated successfully!");
                fetchRetailerProfile();
            } else {
                toast.error(response.message || "Failed to update status");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        } finally {
            setUpdating(false);
        }
    };

    if (!isOpen) return null;

    const getStatusBadge = (status) => {
        const statusMap = {
            'Regular': 'bg-green-100 text-green-800',
            'Active': 'bg-green-100 text-green-800',
            'Inactive': 'bg-yellow-100 text-yellow-800',
            'Suspended': 'bg-red-100 text-red-800',
            'Pending': 'bg-blue-100 text-blue-800',
            'Blocked': 'bg-gray-100 text-gray-800',
            'Deleted': 'bg-purple-100 text-purple-800'
        };
        return statusMap[status] || 'bg-gray-100 text-gray-800';
    };

    const getRoleBadge = (role) => {
        const roleMap = {
            'Admin': 'bg-purple-100 text-purple-800',
            'Manager': 'bg-blue-100 text-blue-800',
            'Retailer': 'bg-green-100 text-green-800',
            'Agent': 'bg-yellow-100 text-yellow-800',
            'Sub-Agent': 'bg-orange-100 text-orange-800',
            'User': 'bg-gray-100 text-gray-800',
            'Distributor': 'bg-indigo-100 text-indigo-800'
        };
        return roleMap[role] || 'bg-gray-100 text-gray-800';
    };

    const getVerificationStatus = (status) => {
        return status === '1' ? (
            <span className="inline-flex items-center text-green-600">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Verified
            </span>
        ) : (
            <span className="inline-flex items-center text-red-600">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Not Verified
            </span>
        );
    };

    const tabs = [
        { id: 'basic', label: 'Basic Information' },
        { id: 'account', label: 'Account Details' },
        { id: 'verification', label: 'Verification' },
        { id: 'security', label: 'Security' }
    ];

    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="relative">
                        <svg
                            className="h-12 w-12"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="#E5E7EB"
                                strokeWidth="1.5"
                            />
                        </svg>
                        <svg
                            className="absolute top-0 left-0 h-12 w-12 animate-spin"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="#2563EB"
                                strokeWidth="1.5"
                                strokeDasharray="30 100"
                                strokeLinecap="round"
                            />
                        </svg>
                    </div>
                    <span className="mt-4 text-sm font-medium text-gray-600 animate-pulse">
                        Loading...
                    </span>
                </div>
            );
        }

        if (error) {
            return (
                <div className="text-center py-20">
                    <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-600">{error}</p>
                    <button
                        onClick={fetchRetailerProfile}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            );
        }

        if (!profileData) {
            return (
                <div className="text-center py-20">
                    <p className="text-gray-500">No data available</p>
                </div>
            );
        }

        switch (activeTab) {
            case 'basic':
                return renderBasicInformation();
            case 'account':
                return renderAccountDetails();
            case 'verification':
                return renderVerification();
            case 'security':
                return renderSecurity();
            default:
                return null;
        }
    };

    const renderTable = (data) => {
        if (!data || Object.keys(data).length === 0) {
            return (
                <div className="text-center py-8 text-gray-500">
                    No data available
                </div>
            );
        }

        return (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {Object.entries(data).map(([key, value]) => (
                            <tr key={key}>
                                <td className="px-4 py-3 text-sm font-medium text-gray-700">{key}</td>
                                <td className="px-4 py-3 text-sm text-gray-900 break-all">{value || 'N/A'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderBasicInformation = () => {
        const data = {
            'User ID': profileData.user_id,
            'Full Name': profileData.name,
            'Email': profileData.email,
            'Mobile': profileData.mobile,
            'IP Address': profileData.ip,
            'Registered Date': profileData.date,
            'Registered Time': profileData.time
        };

        return (
            <div className="space-y-4">
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {Object.entries(data).map(([key, value]) => (
                                <tr key={key}>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{key}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900 break-all">{value || 'N/A'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderAccountDetails = () => {
        const data = {
            'Balance': (
                <span className="font-bold text-green-600">
                    ₹{parseFloat(profileData.balance || 0).toFixed(2)}
                </span>
            ),
            'Role': (
                <span className={`px-2 py-1 inline-flex text-xs font-medium rounded-full ${getRoleBadge(profileData.role)}`}>
                    {profileData.role || 'N/A'}
                </span>
            ),
            'Status': (
                <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${getStatusBadge(profileData.status)}`}>
                    {profileData.status || 'N/A'}
                </span>
            ),
            'Referral Code': profileData.refer_code,
            'Plan Name': profileData.plan_name,
            'Agent ID': profileData.agent_id,
            'Referral By': profileData.refer_by,
            'App ID': profileData.app_id,
            'Last Balance Update': profileData.last_balance_update
        };

        return (
            <div className="space-y-4">
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {Object.entries(data).map(([key, value]) => (
                                <tr key={key}>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{key}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900 break-all">{value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderVerification = () => {
        const data = {
            'Mobile Verification': getVerificationStatus(profileData.mobile_verification),
            'Email Verification': getVerificationStatus(profileData.email_verification),
            'KYC Verification': getVerificationStatus(profileData.kyc_verification)
        };

        return (
            <div className="space-y-4">
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {Object.entries(data).map(([key, value]) => (
                                <tr key={key}>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{key}</td>
                                    <td className="px-4 py-3 text-sm">{value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderSecurity = () => {
        const securityData = {
            'Checksum': profileData.checksum
        };

        // Add OTP information if available
        if (profileData.otp) {
            securityData['OTP'] = profileData.otp;
            securityData['OTP Token'] = profileData.otp_token;
            securityData['OTP Type'] = profileData.otp_type;
            securityData['OTP Time'] = profileData.otp_time;
        }

        if (profileData.token) {
            securityData['Token'] = profileData.token;
        }

        return (
            <div className="space-y-4">
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {Object.entries(securityData).map(([key, value]) => (
                                <tr key={key}>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{key}</td>
                                    <td className="px-4 py-3 text-sm font-mono text-gray-900 break-all">{value || 'N/A'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop with fade */}
            <div
                className="fixed inset-0 bg-black/50 transition-opacity duration-300 ease-in-out"
                onClick={onClose}
                style={{
                    opacity: isOpen ? 1 : 0,
                }}
            ></div>

            {/* Modal with fade and scale */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div
                    className="relative bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col transition-all duration-300 ease-in-out"
                    style={{
                        opacity: isOpen ? 1 : 0,
                        transform: isOpen ? 'scale(1)' : 'scale(0.95)',
                    }}
                >
                    {/* Header - Sticky */}
                    <div className="flex-shrink-0 sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900">
                                Retailer Profile
                            </h3>
                            <p className="text-sm text-gray-500">User ID: #{userId}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Status Update Section - Sticky */}
                    <div className="flex-shrink-0 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-blue-200 sticky top-[73px] z-5">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            Update Status
                        </h4>
                        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                            <div className="flex-1 w-full sm:w-auto">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                                <select
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                                    disabled={updating || loading}
                                >
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                    <option value="Suspended">Suspended</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Blocked">Blocked</option>
                                    <option value="Deleted">Deleted</option>
                                </select>
                            </div>
                            <button
                                onClick={handleStatusUpdate}
                                disabled={updating || loading}
                                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-sm hover:shadow"
                            >
                                {updating ? (
                                    <span className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Updating...
                                    </span>
                                ) : 'Update Status'}
                            </button>
                        </div>
                    </div>

                    {/* Tabs - Sticky */}
                    <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 sticky top-[141px] z-5">
                        <div className="flex space-x-1 overflow-x-auto scrollbar-hide">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`
                                        px-4 py-3 text-sm font-medium transition-all duration-200 whitespace-nowrap
                                        ${activeTab === tab.id
                                            ? 'text-blue-600 border-b-2 border-blue-600'
                                            : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
                                        }
                                    `}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Content - Scrollable */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {renderContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RetailerProfileModal;